import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Card, Dropdown, ListGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Notification from 'components/notification/Notification';
import {
  fetchNotificationsSummary,
  getCachedNotificationsSummary,
  markNotificationsRead,
  subscribeNotificationsSummary
} from 'helpers/notifications';
import { subscribeMessageStream } from 'helpers/messages';

const formatNotificationTime = value => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString();
};

const escapeHtml = value =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const buildNotificationMarkup = notification => {
  const title = escapeHtml(notification.title || '');
  const message = escapeHtml(notification.message || '');
  const metadata = notification.metadata || {};
  const imageUrl = metadata.publication_image_url || '';
  
  let markup = '<strong>' + title + '</strong>';
  
  // Add publication image if available (questions notifications)
  if (imageUrl && metadata.item_id) {
    markup += '<div class="mt-2 mb-2">' +
      '<img src="' + escapeHtml(imageUrl) + '" ' +
      'alt="' + escapeHtml(metadata.publication_title || '') + '" ' +
      'style="max-width: 100%; height: auto; border-radius: 4px; max-height: 120px;" />' +
      '</div>';
  }
  
  if (message) {
    markup += '<div class="text-600 mt-1">' + message + '</div>';
  }
  
  // Add publication title if different from message
  if (metadata.publication_title && metadata.item_id && 
      !message.includes(metadata.publication_title)) {
    markup += '<div class="fs--2 text-500 mt-1"><em>' + 
      escapeHtml(metadata.publication_title) + 
      '</em></div>';
  }
  
  return markup;
};

const NotificationDropdown = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const hasUnread = unreadCount > 0;

  const applySummary = data => {
    const nextData = data || {};
    setNotifications(nextData.notifications || []);
    setUnreadCount(Number(nextData.unread_count || 0));
  };

  const loadSummary = async options => {
    const silent = Boolean(options?.silent);
    const force = Boolean(options?.force);
    setLoading(true);
    try {
      const data = await fetchNotificationsSummary({ force });
      applySummary(data);
      return data;
    } catch (error) {
      console.error('Error loading notifications summary:', error);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const cachedSummary = getCachedNotificationsSummary();
    if (cachedSummary) {
      applySummary(cachedSummary);
    }

    const unsubscribe = subscribeNotificationsSummary(summary => {
      applySummary(summary);
      setLoading(false);
    });

    // Subscribe to SSE for real-time notification counter updates
    let eventSource = null;
    try {
      eventSource = subscribeMessageStream({
        onConversationUpdated: eventData => {
          const { system_notification } = eventData || {};
          if (system_notification) {
            // Increment unread count immediately for system notifications that aren't questions
            // (questions are handled separately in NavCounts)
            setUnreadCount(prev => prev + 1);
          }
        }
      });
    } catch (error) {
      // Silent fail - not critical
    }

    return () => {
      unsubscribe();
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth < 1200) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleToggle = async nextOpenState => {
    setIsOpen(nextOpenState);
    if (nextOpenState) {
      await loadSummary({
        silent: notifications.length > 0,
        force: true
      });
    }
  };

  const markAsRead = async e => {
    e.preventDefault();
    setMarkingAllRead(true);
    try {
      await markNotificationsRead({});
      setNotifications(currentState =>
        currentState.map(notification => ({
          ...notification,
          unread: false
        }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNotificationClick = async notificationId => {
    const target = notifications.find(item => item.id === notificationId);
    if (!target || !target.unread) {
      setIsOpen(false);
      return;
    }

    try {
      await markNotificationsRead({ notification_ids: [notificationId] });
      setNotifications(currentState =>
        currentState.map(notification =>
          notification.id === notificationId
            ? { ...notification, unread: false }
            : notification
        )
      );
      setUnreadCount(currentCount => (currentCount > 0 ? currentCount - 1 : 0));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setIsOpen(false);
    }
  };

  const recentNotifications = useMemo(() => notifications.slice(0, 8), [notifications]);

  return (
    <Dropdown navbar={true} as="li" show={isOpen} onToggle={handleToggle}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className={classNames('px-0 nav-link', {
          'notification-indicator notification-indicator-primary': hasUnread
        })}
      >
        <FontAwesomeIcon icon="bell" transform="shrink-6" className="fs-4" />
        {hasUnread ? (
          <span className="notification-indicator-number">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg">
        <Card
          className="dropdown-menu-notification dropdown-menu-end shadow-none"
          style={{ maxWidth: '20rem' }}
        >
          <FalconCardHeader
            className="card-header"
            title={t('NOTIFICATIONS.DROPDOWN.TITLE')}
            titleTag="h6"
            light={false}
            endEl={
              <Link
                className="card-link fw-normal"
                to="#!"
                onClick={markAsRead}
              >
                {markingAllRead
                  ? t('NOTIFICATIONS.ACTIONS.MARKING')
                  : t('NOTIFICATIONS.ACTIONS.MARK_ALL_READ')}
              </Link>
            }
          />
          <ListGroup
            variant="flush"
            className="fw-normal fs--1 scrollbar"
            style={{ maxHeight: '19rem' }}
          >
            {loading ? (
              <div className="py-4 text-center">
                <Spinner animation="border" size="sm" className="me-2" />
                {t('NOTIFICATIONS.LOADING')}
              </div>
            ) : recentNotifications.length ? (
              recentNotifications.map(notification => (
                <ListGroup.Item key={notification.id}>
                  <Notification
                    flush
                    unread={notification.unread}
                    time={formatNotificationTime(notification.occurred_at || notification.created_at)}
                    to={notification.link || '/social/notifications'}
                    onClick={() => handleNotificationClick(notification.id)}
                  >
                    {buildNotificationMarkup(notification)}
                  </Notification>
                </ListGroup.Item>
              ))
            ) : (
              <div className="py-4 px-3 text-center text-600">
                {t('NOTIFICATIONS.EMPTY')}
              </div>
            )}
          </ListGroup>
          <div
            className="card-footer text-center border-top"
            onClick={() => setIsOpen(false)}
          >
            <Link className="card-link d-block" to="/social/notifications">
              {t('NOTIFICATIONS.ACTIONS.VIEW_ALL')}
            </Link>
          </div>
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
